import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import Footer from "../Footer";
import {
  FiCheck,
  FiLoader,
  FiX
} from "react-icons/fi";
import {
  MdSync,
  MdRestaurantMenu,
  MdAnalytics,
  MdPointOfSale,
  MdUploadFile,
} from "react-icons/md";
import {
  FaBalanceScale,
  FaCalculator,
} from "react-icons/fa";
import { PiBowlFoodBold } from "react-icons/pi";
import { toast } from "react-toastify";
import Select from 'react-select'
import OutletSearch from "../SelectSearch/OutletSearch";
import { formatSalesValue } from "../../utils/formatUtils";

function MenuMaster() {
  const navigate = useNavigate();
  const Url = process.env.REACT_APP_BASE_URL;

  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [token, setToken] = useState("");
  const [selectedOutlet, setSelectedOutlet] = useState();
  const [searchInput, setSearchInput] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);

  const allMenuItems = useMemo(() => {
    const flattenItems = (items) => {
      let result = [];
      items.forEach(item => {
        if (item.items) {
          result = [...result, ...flattenItems(item.items)];
        } else {
          result.push(item);
        }
      });
      return result;
    };

    return flattenItems(menuData);
  }, [menuData]);

  const sortByName = (a, b) => {
    const nameA = a.Name.toLowerCase();
    const nameB = b.Name.toLowerCase();

    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  };

  const filteredItems = useMemo(() => {
    if (selectedItem) return [selectedItem];

    if (typeof searchInput === "object") {
      return [searchInput];
    }

    const searchTerm = searchInput.toLowerCase();

    return [...allMenuItems]
      .filter(item =>
        item.Name.toLowerCase().includes(searchTerm) ||
        (item.Description &&
          item.Description.toLowerCase().includes(searchTerm))
      )
      .sort(sortByName);
  }, [searchInput, allMenuItems, selectedItem]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  useEffect(() => {
    const tokenCheck = localStorage.getItem("token");
    if (!tokenCheck) {
      navigate("/");
    } else {
      setToken(tokenCheck);
    }
  }, [navigate]);

  const fetchMenu = useCallback(async () => {
    if (!token || !selectedOutlet) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(`${Url}/item-lists`, {
        token,
        outlet: selectedOutlet,
      });

      setMenuData(response.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch menu data");
    } finally {
      setLoading(false);
    }
  }, [token, selectedOutlet, Url]);

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  const handleSync = async () => {
    try {
      await axios.post(`${Url}/syncmenu`, {
        token: token,
        outlet: selectedOutlet
      })
      toast.success("Menu synced successfully");
      fetchMenu();
    } catch (error) {
      toast.error("Failed to sync menu data");
      console.log("Sync error", error);
    }
  }

  const renderMenuItems = (items) => (
    <table className="min-w-full table-auto bg-white rounded-lg shadow-lg text-sm">
      <thead className="bg-gray-200">
        <tr>
          <th className="p-2 text-left text-gray-600">Name</th>
          <th className="p-2 text-left text-gray-600">Price</th>
          <th className="p-2 text-left text-gray-600">State</th>
          <th className="pl-2 text-left text-gray-600 whitespace-nowrap">
            Discount Exempt
          </th>
          <th className="p-2 text-gray-600"></th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, itemIndex) => (
          <tr key={itemIndex} className="border-b">
            <td className="p-2 text-gray-700 text-left">
              <button
                className="text-blue-600 text-left"
                onClick={() => {
                  localStorage.setItem("group", item.Description);
                  localStorage.setItem("item", item.Name);
                  navigate("/itemdata");
                }}>
                {item.Name}
              </button>
            </td>
            <td className="p-2 text-gray-700">{formatSalesValue(item.Price)}</td>
            <td className="p-2 text-gray-700">{item.state}</td>
            <td className="p-2 text-gray-700">{item.discountExempt}</td>
            <td className="text-center p-2">
              {item.hasRecipe ? (
                <FiCheck className="text-green-500 text-xl inline" />
              ) : (
                <FiX className="text-red-500 text-xl inline" />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const searchOptions = useMemo(() => {
    return [...allMenuItems]
      .sort(sortByName)
      .map(item => ({
        value: item.Name,
        label: `${item.Name} (${item.Description || "No description"}) - Rs.${item.Price}`,
        data: item
      }));
  }, [allMenuItems]);

  const handleSearchChange = (option) => {
    setSelectedItem(option?.data || null);
    setSearchInput(option?.value || "");
  };

  const filterOption = (option, inputValue) => {
    const search = inputValue.toLowerCase();

    const item = option.data.data;

    const name = item?.Name?.toLowerCase() || "";
    const desc = item?.Description?.toLowerCase() || "";

    return name.includes(search) || desc.includes(search);
  };

  const foodItems = menuData.filter((item) => item.Type === "Food");
  const beverageItems = menuData.filter((item) => item.Type === "Beverage");
  const otherItems = menuData.filter((item) => item.Type === "Other");

  const getButtonStyle = (color) => {
    const colors = {
      slate: "bg-gradient-to-r from-slate-400 to-slate-700",
      blue: "bg-gradient-to-r from-blue-400 to-blue-700",
      green: "bg-gradient-to-r from-green-400 to-green-700",
      yellow: "bg-gradient-to-r from-yellow-400 to-yellow-600",
      orange: "bg-gradient-to-r from-orange-400 to-orange-700",
      sky: "bg-gradient-to-r from-sky-400 to-sky-700",
      pink: "bg-gradient-to-r from-pink-400 to-pink-700",
      purple: "bg-gradient-to-r from-purple-400 to-purple-700",
      indigo: "bg-gradient-to-r from-indigo-400 to-indigo-700",
      teal: "bg-gradient-to-r from-teal-400 to-teal-700",
      lime: "bg-gradient-to-r from-lime-400 to-lime-600",
      amber: "bg-gradient-to-r from-amber-400 to-amber-600",
    };

    return `
    ${colors[color]}
    text-white
    px-3 py-2
    text-sm font-semibold
    rounded-md
    flex items-center gap-1.5
    transition
    hover:scale-[1.03]
    hover:shadow-lg
    active:scale-95
  `;
  };

  // const deleteAllMenu = async () => {
  //   const isConfirmed = window.confirm("Are you sure you want to delete all menu items? This action cannot be undone.");

  //   if (!isConfirmed) {
  //     return; //
  //   }

  //   try {
  //     await axios.post(`${Url}/deleteallmenu`, { token, outlet: selectedOutlet });
  //     setMenuData([]);
  //   } catch (error) {
  //     console.error("Error deleting menu items:", error);
  //   }
  // };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <div className="px-4 py-3 overflow-x-auto overflow-y-auto flex-grow">

        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between mb-2 items-center">
            <OutletSearch
              setSelectedOutlet={setSelectedOutlet}
              selectedOutlet={selectedOutlet}
              token={token}
              compact
            />

            <div className="w-[400px] text-sm">
              <Select
                options={searchOptions}
                onChange={handleSearchChange}
                isClearable
                filterOption={filterOption}
                placeholder="Search menu items..."
                className="basic-single w-full"
                classNamePrefix="select"
              />
            </div>
          </div>

          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <button
              onClick={handleSync}
              className={getButtonStyle("slate")}
            >
              <MdSync className="h-4 w-4" />
              Sync Menu
            </button>

            <button
              onClick={() => handleNavigate('/menu/uploaded-items')}
              className={getButtonStyle("amber")}
            >
              <MdUploadFile className="h-4 w-4" />
              Menu Upload
            </button>

            <button
              onClick={() => handleNavigate('/viewunitdefinitions')}
              className={getButtonStyle("sky")}
            >
              <FaBalanceScale className="h-4 w-4" />
              Unit Definitions
            </button>

            <button
              onClick={() => handleNavigate('/viewsubrecipes')}
              className={getButtonStyle("teal")}
            >
              <PiBowlFoodBold className="h-4 w-4" />
              Sub Recipes
            </button>

            <button
              onClick={() => handleNavigate('/viewrecipeitems')}
              className={getButtonStyle("purple")}
            >
              <MdRestaurantMenu className="h-4 w-4" />
              Recipe Items
            </button>

            <button
              onClick={() => handleNavigate('/menuengineering')}
              className={getButtonStyle("orange")}
            >
              <MdAnalytics className="h-4 w-4" />
              Menu Engineering
            </button>

            <button
              onClick={() => handleNavigate('/inventorycalculation')}
              className={getButtonStyle("indigo")}
            >
              <FaCalculator className="h-4 w-4" />
              Inventory Calculator
            </button>

            <button
              onClick={() => handleNavigate('/inventoryhistory')}
              className={getButtonStyle("pink")}
            >
              <MdPointOfSale className="h-4 w-4" />
              F&B Costing
            </button>
          </div>
        </div>

        {/* <div className="flex justify-center">
          <OutletSearch
            setSelectedOutlet={setSelectedOutlet}
            selectedOutlet={selectedOutlet}
            token={token}
          />
        </div>

        <div className="flex justify-between items-end">
          <div className="w-[400px] text-sm">
            <Select
              options={searchOptions}
              onChange={handleSearchChange}
              isClearable
              filterOption={filterOption}
              placeholder="Search menu items..."
              className="basic-single w-full"
              classNamePrefix="select"
            />
          </div>

          <button
            onClick={handleSync}
            className={getButtonStyle("slate")}
          >
            <MdSync className="h-5 w-5" />
            Sync Menu
          </button>
        </div> */}

        {/* <div className="flex justify-start gap-5 pb-6">
          <button
            onClick={() => navigate("/fileupload")}
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700"
          >
            Upload Menu Data
          </button>

          <button
            onClick={() => navigate("/editmenudata")}
            className="bg-yellow-500 text-white px-6 py-3 rounded-lg hover:bg-yellow-600"
          >
            Edit Menu Data
          </button>

          <button
            onClick={deleteAllMenu}
            className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700"
          >
            Delete All
          </button>
        </div> */}

        <div>
          {loading ? (
            <div className="text-center py-4">
              <FiLoader className="animate-spin h-5 w-5 mr-2 inline-block text-gray-600" />
              Loading data...
            </div>
          ) : error ? (
            <div className="text-red-600 bg-red-50 p-4 text-lg font-semibold max-w-xl mx-auto">
              {error}
            </div>
          ) : menuData.length === 0 ? (
            <p className="text-center text-xl text-red-500">No menu available</p>
          ) : searchInput ? (
            <div className="bg-white rounded-lg shadow-lg px-4 pb-4 max-w-5xl mx-auto mt-6">
              <h2 className="text-lg font-semibold text-gray-700 mb-2">Search Results</h2>
              {filteredItems.length > 0 ? (
                <table className="min-w-full table-auto border text-center text-sm">
                  <thead className="bg-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-gray-600">Name</th>
                      <th className="px-3 py-2 text-gray-600">Price</th>
                      <th className="px-3 py-2 text-gray-600">Category</th>
                      <th className="px-3 py-2 text-gray-600">Type</th>
                      <th className="px-3 py-2 text-gray-600">Status</th>
                      <th className="px-3 py-2 text-gray-600">Discount Exempt</th>
                      <th className="px-3 py-2 text-gray-600">Recipe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item, index) => (
                      <tr key={index} className="border-b">
                        <td className="px-3 py-2 text-gray-700">
                          <button
                            className="text-blue-600"
                            onClick={() => {
                              localStorage.setItem("group", item.Description);
                              localStorage.setItem("item", item.Name);
                              navigate("/itemdata");
                            }}>
                            {item.Name}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-gray-700">{formatSalesValue(item.Price)}</td>
                        <td className="px-3 py-2 text-gray-700">{item.Description}</td>
                        <td className="px-3 py-2 text-gray-700">{item.Type}</td>
                        <td className="px-3 py-2 text-gray-700">{item.state}</td>
                        <td className="px-3 py-2 text-gray-700">{item.discountExempt}</td>
                        <td className="text-center px-3 py-2">
                          {item.hasRecipe ? (
                            <FiCheck className="text-green-500 text-xl inline" />
                          ) : (
                            <FiX className="text-red-500 text-xl inline" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-center text-gray-500">No items found matching your search</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <div>
                <div className="text-lg font-semibold text-gray-700">Food</div>
                {foodItems.length > 0 ? (
                  foodItems.map((category, index) => (
                    <div key={index} className="mb-6">
                      {category.items &&
                        category.items.length > 0 &&
                        category.items.map((subCategory, subIndex) => (
                          <div key={subIndex} className="mb-4">
                            <div className="text-base font-medium text-gray-600 mb-1">
                              {subCategory.Description}
                            </div>
                            {subCategory.items &&
                              subCategory.items.length > 0 &&
                              renderMenuItems(subCategory.items)}
                          </div>
                        ))}
                    </div>
                  ))
                ) : (
                  <p>No food items available</p>
                )}
              </div>

              <div>
                <div className="text-lg font-semibold text-gray-700">Beverage</div>
                {beverageItems.length > 0 ? (
                  beverageItems.map((category, index) => (
                    <div key={index} className="mb-6">
                      {category.items &&
                        category.items.length > 0 &&
                        category.items.map((subCategory, subIndex) => (
                          <div key={subIndex} className="mb-4">
                            <div className="text-base font-medium text-gray-600 mb-1">
                              {subCategory.Description}
                            </div>
                            {subCategory.items &&
                              subCategory.items.length > 0 &&
                              renderMenuItems(subCategory.items)}
                          </div>
                        ))}
                    </div>
                  ))
                ) : (
                  <p>No beverage items available</p>
                )}
              </div>

              <div>
                <div className="text-lg font-semibold text-gray-700">Others</div>
                {otherItems.length > 0 ? (
                  otherItems.map((category, index) => (
                    <div key={index} className="mb-6">
                      {category.items &&
                        category.items.length > 0 &&
                        category.items.map((subCategory, subIndex) => (
                          <div key={subIndex} className="mb-4">
                            <div className="text-base font-medium text-gray-600 mb-1">
                              {subCategory.Description}
                            </div>
                            {subCategory.items &&
                              subCategory.items.length > 0 &&
                              renderMenuItems(subCategory.items)}
                          </div>
                        ))}
                    </div>
                  ))
                ) : (
                  <p>No other items available</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default MenuMaster;
